import { UseQueryOptions, UseSuspenseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { getZaplifySdk } from '@zaplify/sdk';
import { createMutationOptions } from './sdk-utils';
import { GetPendingInvitationsResponse, GetConnectionsResponse } from '@andsend/services-linkedin-profiles-shared';
const fetchConnectionData = (memberIdOrPublicIdentifier: string) => {
    return getZaplifySdk().profiles.linkedinConnections.getConnection(memberIdOrPublicIdentifier);
};
type ConnectionData = Awaited<ReturnType<typeof fetchConnectionData>>;
export const getConnection = (
    memberIdOrPublicIdentifier: string,
    options?: Omit<
        UseQueryOptions<ConnectionData, Error> & UseSuspenseQueryOptions<ConnectionData, Error>,
        'queryKey' | 'queryFn'
    >
): UseQueryOptions<ConnectionData, Error> & UseSuspenseQueryOptions<ConnectionData, Error> => {
    return {
        queryKey: ['getConnection', memberIdOrPublicIdentifier],
        queryFn: () => fetchConnectionData(memberIdOrPublicIdentifier),
        enabled: !!memberIdOrPublicIdentifier,
        ...options,
    };
};

const fetchConnectionStatusFn = (memberId: string) => {
    return getZaplifySdk().profiles.linkedinConnections.getConnectionStatus(memberId);
};
type ConnectionStatusData = Awaited<ReturnType<typeof fetchConnectionStatusFn>>;
export const getConnectionStatus = (
    memberId: string,
    options?: Omit<
        UseQueryOptions<ConnectionStatusData, Error> & UseSuspenseQueryOptions<ConnectionStatusData, Error>,
        'queryKey' | 'queryFn'
    >
): UseQueryOptions<ConnectionStatusData, Error> & UseSuspenseQueryOptions<ConnectionStatusData, Error> => {
    return {
        queryKey: ['getConnectionStatus', memberId],
        queryFn: () => fetchConnectionStatusFn(memberId),
        enabled: !!memberId,
        ...options,
    };
};

const addPendingConnectionsFn = (params: { connections: GetPendingInvitationsResponse }) => {
    return getZaplifySdk().profiles.linkedinConnections.createPendingConnections(params.connections);
};
type AddPendingConnectionsParams = Parameters<typeof addPendingConnectionsFn>[0];
type AddPendingConnectionsData = Awaited<ReturnType<typeof addPendingConnectionsFn>>;
export const addPendingConnections = (
    options?: Omit<UseMutationOptions<AddPendingConnectionsData, Error, AddPendingConnectionsParams>, 'mutationFn'>
): UseMutationOptions<AddPendingConnectionsData, Error, AddPendingConnectionsParams> => {
    return {
        mutationFn: (connections) => addPendingConnectionsFn(connections),
        ...options,
    };
};

const addConnectionsFn = (params: { connections: GetConnectionsResponse }) => {
    return getZaplifySdk().profiles.linkedinConnections.createConnections(params.connections);
};
type AddConnectionsParams = Parameters<typeof addConnectionsFn>[0];
type AddConnectionsData = Awaited<ReturnType<typeof addConnectionsFn>>;
export const addConnections = (
    options?: Omit<UseMutationOptions<AddConnectionsData, Error, AddConnectionsParams>, 'mutationFn'>
): UseMutationOptions<AddConnectionsData, Error, AddConnectionsParams> => {
    return {
        mutationFn: (connections) => addConnectionsFn(connections),
        ...options,
    };
};

const fetchConnectionSyncProgressFn = () => {
    return getZaplifySdk().profiles.linkedinConnections.getConnectionSyncProgress();
};
type ConnectionSyncProgressData = Awaited<ReturnType<typeof fetchConnectionSyncProgressFn>>;
export const getConnectionSyncProgress = (
    options?: Omit<
        UseQueryOptions<ConnectionSyncProgressData, Error> & UseSuspenseQueryOptions<ConnectionSyncProgressData, Error>,
        'queryKey' | 'queryFn'
    >
): UseQueryOptions<ConnectionSyncProgressData, Error> & UseSuspenseQueryOptions<ConnectionSyncProgressData, Error> => {
    return {
        queryKey: ['getConnectionSyncProgress'],
        queryFn: () => fetchConnectionSyncProgressFn(),
        ...options,
    };
};
